import type { ServiceItem } from "../types/service";
import type { CollageWorkItem } from "../types/work";

/**
 * Written recommendations, quoted verbatim from the source portfolio.
 * `videoUrl` is optional: add a real MP4/WebM of a video recommendation and the card
 * gains a play control that opens it in the video dialog. Nothing is substituted otherwise.
 */
export type Testimonial = {
  videoUrl?: string;
  videoAspect?: "landscape" | "portrait" | "square";
  /** Supplied portrait. Cards without one fall back to the person's initials. */
  avatar?: string;
  quote: string;
  name: string;
  role: string;
};

export const links = {
  calendly: "https://calendly.com/s-m-zeeshan2233/30min",
  linkedin: "https://www.linkedin.com/in/syed-muhammad-zeeshan-283a2550/",
  whatsapp: "https://wa.me/97339864230?text=Hi%20Zeeshan%2C%20I%20found%20your%20website%20and%20would%20like%20to%20discuss%20a%20project",
  email: "mailto:hello@smzeeshan.com",
  sourceArchive: "https://orchid-path-140706.framer.app/videos",
};

export const brands = [
  "Aramco", "Alba", "BAC", "BAPCO", "Batelco", "BBK", "CNBC Arabia", "Dubai RTA", "Bahrain Duty Free",
  "GPIC", "Gulf Air", "Innowi", "Isuzu KSA", "MINISO", "NBB", "Nesma & Partners", "BisB",
  "Racing Force Group", "SINOPEC", "stc", "talabat", "Urbaser", "Arabian Drilling", "Uber Eats",
];

/**
 * Supplied client marks, shown as a static grid. Tiles are dark because the artwork is white
 * on transparent, which would be invisible on the light brands band.
 */
export const brandLogos: { name: string; src: string; keepColor?: boolean; scale?: number }[] = [
  { name: "Aramco", src: "https://cdn.magicpatterns.com/uploads/2kTrJgzGJZzqgd2ZX1jhRo/ARAMCO.png", keepColor: true },
  { name: "BAC", src: "https://cdn.magicpatterns.com/uploads/k9CWPZaxQvZqxMmRbB3mSB/BAC.png" },
  { name: "BAPCO", src: "https://cdn.magicpatterns.com/uploads/tF7dP3su2rQswj3HEr1Meo/BAPCO.png" },
  { name: "BBK", src: "https://cdn.magicpatterns.com/uploads/1J3hb2opcSHYYYw3ZUvp83/BBK_Bank.png" },
  { name: "Batelco", src: "https://cdn.magicpatterns.com/uploads/cBPhxMtB75o8Ex13W1kRix/Batelco.png" },
  { name: "Bahrain Duty Free", src: "https://cdn.magicpatterns.com/uploads/cacPpd12FfNxgVPfNwewWk/DUTYfree.png" },
  { name: "CNBC Arabia", src: "https://cdn.magicpatterns.com/uploads/5JvmbPcqrye8vePGktSE4s/CNBC.png" },
  { name: "Dubai RTA", src: "https://cdn.magicpatterns.com/uploads/ksoZvtWu3nSdPTyeDDL4sg/Dubai_RTA.png", keepColor: true },
  { name: "GPIC", src: "https://cdn.magicpatterns.com/uploads/7H6ggHWRJYLWDiZQYLg4UZ/GPIC.png" },
  { name: "Gulf Air", src: "https://cdn.magicpatterns.com/uploads/e9hexgVhX3tcBMvfLPnrw6/GulfAir.png" },
  { name: "Innowi", src: "https://cdn.magicpatterns.com/uploads/p2XBQzYfHVQzCp5anv1mkT/Innowi.png" },
  { name: "Isuzu KSA", src: "https://cdn.magicpatterns.com/uploads/gpEMr75Cuqu1wQwr9pAseX/ISUZU.png" },
  { name: "MINISO", src: "https://cdn.magicpatterns.com/uploads/f9EBr7t4RpMt7osP8qKxmT/MINISO.png", scale: 0.78 },
  { name: "NBB", src: "https://cdn.magicpatterns.com/uploads/t1eU44hVTBFu56kbJnJYER/NBB_bank.png" },
  { name: "Nesma & Partners", src: "https://cdn.magicpatterns.com/uploads/68LcXeB9jtXKCk8a5QAVUE/Nesma.png" },
  { name: "BisB", src: "https://cdn.magicpatterns.com/uploads/8atoaT2sk2VsoJCkG5Ls2V/Bis_(1).png", keepColor: true, scale: 0.9 },
  { name: "Racing Force Group", src: "https://cdn.magicpatterns.com/uploads/kQ6saAGTLWhXKJaw8h91zR/RACING_FORCE.png" },
  { name: "SINOPEC", src: "https://cdn.magicpatterns.com/uploads/jQE8Kqj5cbotL77aJKcpQY/Sinopec.png" },
  { name: "stc", src: "https://cdn.magicpatterns.com/uploads/hhnpvHe5gtHS5YP6V9njqW/STC.png" },
  { name: "talabat", src: "https://cdn.magicpatterns.com/uploads/4HDUBBFqn8ocwBeMrneSvz/TALABAT.png" },
  { name: "Uber Eats", src: "https://cdn.magicpatterns.com/uploads/eZabi1pkFnvPeRHDGykGa2/UBEREAST.png" },
  { name: "Urbaser", src: "https://cdn.magicpatterns.com/uploads/cAegvvV29kPSjVhtSywQRb/Urbaser.png" },
  { name: "Arabian Drilling", src: "https://cdn.magicpatterns.com/uploads/ahnGDywACibqAmmfNnwuws/AD_logo_(1).png", keepColor: true, scale: 0.7 },
];

/** The workflow statement shown beneath the selected-work collage and on the portfolio page. */
export const workApproach: {
  heading: { start: string; accent: string };
  points: { title?: string; description: string }[];
} = {
  heading: { start: "Workflow &", accent: "Delivery" },
  points: [
    { description: "From initial concept to final render." },
    {
      title: "3D Design & CGI Visualization",
      description:
        "High-detail product design, lighting, texturing, and cinematic 3D renders using Cinema 4D, Octane, and Redshift.",
    },
    {
      title: "Motion & Post-Production",
      description:
        "Dynamic motion graphics, video editing, voice integration, and high-impact visual effects for commercial and broadcast media.",
    },
  ],
};

/**
 * Collage entries. Titles, disciplines and tags come only from the source portfolio.
 * `videoUrl` is intentionally empty — add a real MP4/WebM and the card autoplays it on scroll-idle.
 */
export const workItems: CollageWorkItem[] = [
  { id: "film-01", index: "001", kind: "FILM", title: "Showreel", tags: ["MOTION", "POST"], aspect: "landscape", plate: "motionPlate", vimeoId: "1209590483", position: { column: 0, drift: -46 } },
  { id: "film-02", index: "002", kind: "FILM", title: "Emirates NBD Financial Wellness", tags: ["MOTION", "EDIT"], aspect: "landscape", plate: "archivePlate", vimeoId: "1220435552", position: { column: 1, drift: -30 } },
  { id: "film-03", index: "003", kind: "REEL", title: "CGI Reel (BBK Bank)", tags: ["MOTION", "POST"], aspect: "portrait", plate: "materialStudy", vimeoId: "1214515604", position: { column: 2, drift: -38 } },
  { id: "reel-vfx", index: "004", kind: "REEL", title: "CGI Reel for Bahrain Duty Free", tags: ["VFX", "COMPOSITING", "CLEANPLATE"], aspect: "portrait", plate: "vfxPlate", vimeoId: "1178003422", position: { column: 0, drift: 28 } },
  // Playback shape resolves from the source film itself.
  { id: "film-05", index: "005", kind: "REEL", title: "STC Beam Campaign Reel", tags: ["EDIT", "POST"], aspect: "portrait", vimeoId: "1220594505", position: { column: 1, drift: 32 } },
  { id: "film-07", index: "006", kind: "REEL", title: "Table Top QR Ordering", tags: ["MOTION", "VFX"], aspect: "square", vimeoId: "1220450526", position: { column: 2, drift: -34 } },
  { id: "housing-summit", index: "007", kind: "REEL", title: "Housing Summit Teaser", tags: ["MOTION", "EDIT"], aspect: "portrait", vimeoId: "1234592282", position: { column: 0, drift: 26 } },
  { id: "naseej-society", index: "008", kind: "FILM", title: "Naseej Madinat Khalifa Society", tags: ["FILM", "EDIT"], aspect: "landscape", vimeoId: "1234594437", position: { column: 1, drift: -30 } },
  { id: "presidential-cup", index: "009", kind: "FILM", title: "Branding of Presidential Cup (UAE) 2026", tags: ["MOTION", "BRANDING"], aspect: "landscape", vimeoId: "1234596470", position: { column: 0, drift: 20 } },
  { id: "arabian-drilling-chro", index: "010", kind: "REEL", title: "Bisb Gift Video", tags: ["FILM", "POST"], aspect: "portrait", vimeoId: "1234598907", position: { column: 1, drift: -22 } },
  { id: "dubai-frame-cgi", index: "011", kind: "REEL", title: "Dubai Frame Cloth Reveal CGI", tags: ["CGI", "VFX"], aspect: "portrait", vimeoId: "1214520640", position: { column: 2, drift: -26 } },
];

/**
 * "What I do best" services. Copy is derived from the source portfolio's own service
 * descriptions; `videoUrl` is intentionally empty — drop in a real MP4/WebM per service
 * and the flip card autoplays it muted on loop.
 */
export const services: ServiceItem[] = [
  {
    id: "cgi",
    number: "01",
    label: "Signature craft",
    title: "3D Animation, CGI & VFX",
    description: "Product visualisation, environment design, cinematic lighting, realistic rendering, simulations, and VFX compositing for commercial and broadcast work.",
    capabilities: ["Product visualisation", "Environment design", "Cinematic lighting", "Octane rendering", "Simulations", "Cinema 4D"],
    plate: "cgiPlate",
  },
  {
    id: "character",
    number: "02",
    label: "2D / Character",
    title: "2D Character Animation & Explainers",
    description: "Storyboarding, illustration, character design, rigging, animation, compositing, and final rendering for commercials, explainers, corporate, educational, and social content.",
    capabilities: ["Storyboarding", "Character design", "Rigging", "Frame animation", "Explainer videos", "After Effects"],
    plate: "characterPlate",
  },
  {
    id: "motion",
    number: "03",
    label: "Motion / Post",
    title: "Motion Graphics & Post-Production",
    description: "Commercial motion graphics and post-production for campaigns, broadcast idents, social media, and corporate films, built to hold up at any delivery size.",
    capabilities: ["Motion design", "Broadcast idents", "Titles & typography", "FOOH advertising", "Colour & finishing", "Delivery masters"],
    plate: "motionPostPlate",
  },
  {
    id: "vfx",
    number: "04",
    label: "AI-assisted VFX",
    title: "Commercial VFX & Compositing",
    description: "Clean plates, precision tracking, advanced compositing, CGI integration, rotoscoping, and photoreal visual enhancements using AI alongside established VFX techniques.",
    capabilities: ["Cleanplating", "Rotoscoping", "Tracking", "CGI integration", "AI-assisted comp", "Photoreal finishing"],
    plate: "commercialVfxPlate",
  },
  {
    id: "edit",
    number: "05",
    label: "Edit / Delivery",
    title: "Long & Short-Form Video",
    description: "Editing for documentaries, podcasts, interviews, corporate films, YouTube, commercials, training content, and social media delivery.",
    capabilities: ["Long-form edit", "Short-form edit", "Interviews & podcasts", "Corporate films", "Platform versioning", "Aspect adaptation"],
    plate: "videoFormatPlate",
  },
  {
    id: "consultancy",
    number: "06",
    label: "Direction",
    title: "Creative Consultancy",
    description: "Creative direction, brand storytelling, campaign ideation, content strategy, visual identity, production planning, and AI-powered creative workflows, from concept to final execution.",
    capabilities: ["Creative direction", "Brand storytelling", "Campaign ideation", "Content strategy", "Production planning", "AI workflows"],
    plate: "consultancyPlate",
  },
  {
    id: "live-action",
    number: "07",
    label: "Live action",
    title: "Live Action & Video Production",
    description: "On-camera production handled end to end: studio and camera work, autonomously operated DSLR and cinema camera setups, full lighting and sound setup, green screen shoots, and B-roll capture.",
    capabilities: ["Studio/camera work", "Autonomous DSLR/cinema camera setups", "Studio setup (lighting/sound)", "Green screen shoot", "B-roll capture"],
    plate: "liveActionPlate",
  },
  {
    id: "product",
    number: "08",
    label: "Product",
    title: "UI UX / Product Design",
    description: "Interface and product design carrying the same craft as the film work: user flows, wireframes, design systems, responsive layouts, prototyping, and motion-led interaction design.",
    capabilities: ["User flows", "Wireframes", "Design systems", "Responsive layouts", "Prototyping", "Interaction design"],
    plate: "productDesignPlate",
  },
];

export const skillGroups = [
  { label: "CREATE & CAPTURE", skills: ["Creative Direction", "Storyboarding", "Branding", "Content Strategy", "Creative Consultancy", "Studio Shooting"] },
  { label: "BUILD", skills: ["CGI", "2D/3D Animation", "FOOH Advertising", "Explainer Videos", "Cinema 4D", "Octane Render"] },
  { label: "MOTION", skills: ["Infographics", "Motion Graphics", "Character Animation", "After Effects"] },
  { label: "COMPOSITE", skills: ["Visual Effects", "Compositing", "Rotoscoping", "Cleanplating", "CGI Integration", "Keying", "Green Screen Shoot"] },
  { label: "ACCELERATE", skills: ["AI Workflow Integration", "Generative AI Tools", "Adobe Firefly", "Adobe Express", "Canva AI", "Higgsfield", "Kling", "Runway"] },
  { label: "DELIVER", skills: ["Video Editing", "Post-Production", "B-Roll Capture", "Client Communication", "Quality Assurance"] },
];

export const testimonials: Testimonial[] = [
  { quote: "It was a wonderful working experience with Zeeshan. He’s a dedicated & talented professional with excellent skills in VFX clean-up, and rotoscoping. His attention to detail, reliability, and positive attitude truly stand out. Highly recommended!", name: "Vivian Pinto", role: "Managing Director at Ladder Frame Films WLL and int’l Line Producer/Coordinator at Netflix & Marvel Entertainment", avatar: "https://cdn.magicpatterns.com/uploads/n8MSNzwB3THibE8P3scYWZ/VIVIAN.jpg" },
  { quote: "Zeeshan was more than just a CG artist. He was a true creative partner. His problem-solving mindset, flexibility, and professionalism made the project smooth from start to finish. He was reliable, collaborative, and always focused on delivering the best possible result.", name: "Fadhi Muhammad", role: "Founder/Director FPS Solutions W.L.L", avatar: "https://cdn.magicpatterns.com/uploads/6k8pGqoK4Yb4qHHJc2Mi9U/Fadhi.jpg" },
  { quote: "Working with Zeeshan was a fantastic experience. He brings a great mix of creativity, technical expertise, and attention to detail to every project. Even with tight timelines, he consistently delivered high-quality work and was always responsive and easy to collaborate with. A dependable creative professional I’d gladly recommend!", name: "Angee Soon", role: "Co-founder and Creative Director of The Ikan Bilis Marketing W.L.L.", avatar: "https://cdn.magicpatterns.com/uploads/8pnMj2efR8WGeET72CyGxU/Screenshot_2026-08-11_172633.jpg" },
  { quote: "Zeeshan was a great asset to our team at Innowi from 2021–2024. His ability to turn ideas into engaging motion content, combined with his strong creative thinking, made a real impact on our projects. He was always supportive, professional, & easy to collaborate with. I’d definitely recommend him for any animation or creative role.", name: "Maheen Adil", role: "Head of Design | Product UX · Brand · Web | Product Design in Restaurant Tech", avatar: "https://cdn.magicpatterns.com/uploads/41N8ybQaWuJSU2dV5LGXoB/1691645622477.jpg" },
  { quote: "Working with Zeeshan at Liwa was genuinely a pleasure. He’s talented, dependable, and always brings a positive energy to the team. I’d happily work with him again anytime, highly recommended!.", name: "Muhammad Shoaib", role: "Head of Animation/Motion Design at LIWA", avatar: "https://cdn.magicpatterns.com/uploads/n9egTGVLGCczQ9opCTwWkH/1719320162358.jpg" },
  { quote: "I had the pleasure of working with Zeeshan on a CNBC Arabia program ident. He’s a talented and dependable Motion Graphics Designer with a strong creative mindset and solid technical skills. He consistently delivers polished, high-quality work and is a pleasure to collaborate with. Highly recommended.", name: "Shahrukh Sidiki", role: "EX Creative Manager of CNBC ARABIA | Creative Consultant | Vizrt Specialist | AI Content Creator", avatar: "https://cdn.magicpatterns.com/uploads/7svYaKQicujVQh6ffmdDCH/Shahrukh.jpg" },
];

/**
 * FAQ. Questions are the source portfolio's own. Answers summarise only what the source
 * already publishes — the service descriptions, the skill list, and the biography — so
 * nothing here claims anything beyond that.
 */
export const faqs = [
  {
    question: "I have a great video idea, but I’m not sure how to get started. Can you help?",
    answer:
      "Yes, that is exactly what the creative consultancy work covers: creative direction, brand storytelling, campaign ideation, content strategy, and production planning. An idea gets shaped into a plan before a single frame is built. A call is the quickest way in.",
  },
  {
    question: "What services do you specialize in?",
    answer:
      "Eight capabilities, run through one pipeline: 3D animation, CGI and VFX; 2D character animation and explainers; motion graphics and post-production; commercial VFX and compositing; long and short-form video; creative consultancy; live action and video production; and UI UX / product design.",
  },
  {
    question: "What software and 3D rendering engines do you use?",
    answer:
      "Cinema 4D with Octane Render for 3D build, lighting and rendering, and After Effects for animation, compositing and finishing, with AI-assisted tools working inside the same pipeline rather than beside it.",
  },
  {
    question: "Do you handle complex Visual Effects like Rotoscoping and Cleanplating?",
    answer:
      "Yes. Rotoscoping, cleanplating, precision tracking, CGI integration and advanced compositing are core parts of the commercial VFX work, including VFX clean-up on production footage.",
  },
  {
    question: "How do you incorporate AI into your animation and post-production workflow?",
    answer:
      "AI-assisted tools sit inside the existing Cinema 4D and After Effects pipeline rather than replacing it. They speed up the repetitive stages such as clean-up, versioning and iteration, while lighting, rendering, compositing and final grade stay under manual control so visual fidelity never drops.",
  },
  {
    question: "Can you adapt video edits and motion design for different aspect ratios and platforms?",
    answer:
      "Yes. Platform versioning and aspect adaptation are part of the long and short-form delivery work, so one edit can be mastered for broadcast, YouTube, corporate use and vertical social formats: 16:9, 1:1 and 9:16.",
  },
  {
    question: "How does the creative collaboration process work from start to finish?",
    answer:
      "As one continuous pipeline: concept and direction, design and storyboarding, 3D build, animation, compositing, then finishing and delivery, with a review point at each stage.",
  },
];
